import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { isAfter, endOfToday, differenceInDays, startOfDay } from 'date-fns'
import { useDocuments } from 'contexts/documents'
import { compact } from 'utilities/array'
import { Actions } from './s'
import { Button, Ghost } from 'components/button'
import StandaloneTimePicker from 'components/form/field/time/standalone'
import Prompt from 'prompts/standard'

const SigningActions = ({ dokument, setConfiguringSigning, salt }) => {
    const { formatMessage } = useIntl()

    const [postponing, setPostponing] = useState(false)
    const [withdrawing, setWithdrawing] = useState(false)
    const [destructing, setDestructing] = useState(false)
    const [prompting, setPrompting] = useState(null)

    const { signing } = dokument

    const {
        status,
        dueAt
    } = signing

    const {
        postponeSigningDeadline,
        withdrawSigning,
        removeSigning
    } = useDocuments()

    if(!['pending', 'rejected', 'timedout', 'canceled'].includes(status)) {
        return null
    }

    const postpone = async date => {
        setPostponing(true)

        const days = differenceInDays(startOfDay(new Date(date)), startOfDay(new Date(dueAt)))
        if(!days) {
            return
        }

        await postponeSigningDeadline(days, dokument.id)

        setPostponing(false)
    }

    const withdraw = async () => {
        setWithdrawing(true)
        await withdrawSigning(dokument.id)
        setWithdrawing(false)
    }

    const remove = async () => {
        setDestructing(true)
        await removeSigning(dokument.id)
        setDestructing(false)
    }

    const now = new Date()

    const whatToPromptMap = {
        withdraw: {
            question: {
                id: 'document_signing_action_withdraw_prompt_question',
                defaultMessage: 'Confirm withdrawing this signing process'
            },
            confirm: {
                id: 'document_signing_action_withdraw',
                defaultMessage: 'Withdraw'
            },
            callback: withdraw
        }
    } ?? {}

    const Postpone = () => ['timedout', 'pending'].includes(status) ? (
        <StandaloneTimePicker
            time={dueAt ? new Date(dueAt) : now}
            past={false}
            blockedDateRanges={compact([
                { on: now },
                (dueAt && isAfter(new Date(dueAt), endOfToday())) && {
                    start: now,
                    end: new Date(dueAt)
                }
            ])}
            trigger={(
                <Ghost
                    className={`constructive${postponing ? ' loading' : ''}`}
                    disabled={withdrawing}>
                    <FormattedMessage
                        id="document_signing_action_postpone_deadline"
                        defaultMessage="Postpone deadline" />
                </Ghost>
            )}
            label={formatMessage({
                id: 'document_signing_action_postpone_deadline',
                defaultMessage: 'Postpone signing deadline'
            })}
            onChange={postpone}
            salt={`${salt}:postpone-signing-deadline`} />
    ) : null

    const Withdraw = () => (status === 'pending') ? (
        <Ghost
            onClick={() => setPrompting({ what: 'withdraw' })}
            className={`destructive${withdrawing ? ' loading' : ''}`}
            disabled={postponing || prompting}>
            <FormattedMessage
                id="document_signing_action_withdraw"
                defaultMessage="Withdraw" />
        </Ghost>
    ) : null

    const Restart = () => ['rejected', 'timedout', 'canceled'].includes(status) ? (
        <Button
            onClick={() => setConfiguringSigning({ dokument })}
            className="constructive"
            disabled={destructing}>
            <FormattedMessage
                id="document_signing_action_restart"
                defaultMessage="Restart signing" />
        </Button>
    ) : null

    const Remove = () => ['rejected', 'timedout', 'canceled'].includes(status) ? (
        <Ghost
            onClick={remove}
            className={`destructive${destructing ? ' loading' : ''}`}>
            <FormattedMessage
                id="document_signing_action_remove"
                defaultMessage="Remove signing" />
        </Ghost>
    ) : null

    return (
        <Actions>
            <Postpone />
            <Restart />
            <Withdraw />
            <Remove />
            <Prompt
                show={prompting}
                salt={`${salt}:prompt:${prompting?.what ?? null}`}
                {...(prompting ? {
                    question: formatMessage(whatToPromptMap[prompting.what].question),
                    cancel: {
                        text: formatMessage({
                            id: 'action_cancel',
                            defaultMessage: 'Cancel'
                        }),
                        callback: () => setPrompting(null)
                    },
                    confirm: {
                        text: formatMessage(whatToPromptMap[prompting.what].confirm),
                        effect: 'destructive',
                        callback: whatToPromptMap[prompting.what]?.callback
                    }
                } : null)} />
        </Actions>
    )
}

export default SigningActions
